import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { ShoppingBag } from "lucide-react";
import { MERCH_URL } from "../lib/constants";
import { NextDropTicker } from "./NextDropTicker";

const links = [
    { href: "#about", label: "About" },
    { href: "#episodes", label: "Episodes" },
    { href: "#music", label: "Music" },
    { href: "#coaching", label: "Coaching" },
    { href: "#prayer", label: "Prayer" },
    { href: "#join", label: "Join" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-nav"
            className={`sticky top-0 left-0 right-0 z-40 transition-all duration-500 ${
                scrolled ? "py-3" : "py-6"
            }`}
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <div
                    className={`flex items-center justify-between rounded-full px-5 md:px-7 py-3 ${
                        scrolled ? "glass shadow-[0_8px_32px_rgba(43,29,53,0.06)]" : ""
                    }`}
                >
                    <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
                        <span className="grid place-items-center w-9 h-9 rounded-full bg-gold text-white font-display text-lg leading-none">
                            H
                        </span>
                        <span className="font-display text-base md:text-lg tracking-tight text-lilac-900">
                            Becoming <span className="italic">H.E.R.</span>
                        </span>
                    </a>

                    <nav className="hidden md:flex items-center gap-1">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className="nav-pill px-4 py-2 rounded-full text-sm text-lilac-700 hover:text-lilac-900"
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <NextDropTicker />
                        <Button
                            data-testid="nav-merch-btn"
                            asChild
                            variant="outline"
                            className="hidden sm:inline-flex rounded-full border-lilac-300 bg-white/70 hover:bg-white text-lilac-900 px-4 h-10"
                        >
                            <a href={MERCH_URL} target="_blank" rel="noopener noreferrer">
                                <ShoppingBag className="w-4 h-4" /> Shop Merch
                            </a>
                        </Button>
                        <Button
                            data-testid="nav-listen-btn"
                            asChild
                            className="hidden sm:inline-flex rounded-full bg-gold hover:bg-gold-deep text-white px-5 h-10 shadow-[0_8px_24px_rgba(212,175,55,0.35)]"
                        >
                            <a href="#episodes">Listen Now</a>
                        </Button>
                        <button
                            data-testid="nav-mobile-toggle"
                            className="md:hidden p-2 rounded-full border border-border"
                            onClick={() => setOpen((v) => !v)}
                            aria-label="Toggle menu"
                        >
                            <span className="block w-5 h-px bg-lilac-900 mb-1" />
                            <span className="block w-5 h-px bg-lilac-900 mb-1" />
                            <span className="block w-5 h-px bg-lilac-900" />
                        </button>
                    </div>
                </div>

                {open && (
                    <div className="md:hidden mt-2 glass rounded-3xl p-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="px-4 py-3 rounded-2xl text-sm text-lilac-900 hover:bg-lilac-100"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href={MERCH_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                            data-testid="nav-mobile-merch"
                            className="mt-1 px-4 py-3 rounded-2xl text-sm text-lilac-900 hover:bg-lilac-100 inline-flex items-center gap-2"
                        >
                            <ShoppingBag className="w-4 h-4" /> Shop Merch
                        </a>
                    </div>
                )}
            </div>
        </header>
    );
};
